import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, RefreshCw, Info } from "lucide-react";

/**
 * PixStatusWidget — Card no dashboard admin mostrando se o Stripe já liberou
 * PIX na conta. Verde = ativo (PIX está funcionando); Vermelho = ainda não.
 * Botão "Verificar agora" força uma checagem imediata (dispara /cron/pix-check).
 * Auto-refresh a cada 60s enquanto a página está aberta.
 */
const PixStatusWidget = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/pix-status");
      setStatus(data);
    } catch (e) {
      // silencioso — endpoint pode não existir em ambientes antigos
    } finally {
      setLoading(false);
    }
  };

  const checkNow = async () => {
    setChecking(true);
    try {
      await api.post("/admin/pix-status/check");
      toast.success("Checagem disparada — aguarde 3-5 segundos");
      setTimeout(load, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao disparar checagem");
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);  // 60s
    return () => clearInterval(t);
  }, []);

  if (loading || !status) return null;

  const ready = status.pix_stripe_ready;
  const lastAt = status.last_checked
    ? new Date(status.last_checked).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })
    : "nunca";

  return (
    <div
      className={`rounded-xl border-2 p-4 ${
        ready ? "border-emerald-400 bg-gradient-to-br from-emerald-50 to-white"
              : "border-amber-300 bg-gradient-to-br from-amber-50 to-white"
      }`}
      data-testid="pix-status-widget"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
            ready ? "bg-emerald-500" : "bg-amber-500"
          }`}>
            {ready ? <CheckCircle2 className="w-5 h-5 text-white" />
                   : <XCircle className="w-5 h-5 text-white" />}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-black text-slate-500">
              Status PIX no Stripe
            </div>
            <div className={`text-base font-display font-bold mt-0.5 ${
              ready ? "text-emerald-700" : "text-amber-800"
            }`} data-testid="pix-status-label">
              {ready ? "✅ PIX Ativado — funcionando no checkout"
                     : "⚠ PIX ainda não liberado pela Stripe"}
            </div>
            <div className="text-[11px] text-slate-500 mt-1">
              Última checagem: <b>{lastAt}</b>
            </div>
            {!ready && status.last_error && (
              <details className="mt-2 text-[10px] text-slate-500">
                <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                  Ver detalhes do erro Stripe
                </summary>
                <div className="mt-1 p-2 bg-slate-100 rounded font-mono break-all">
                  {status.last_error}
                </div>
              </details>
            )}
          </div>
        </div>
        <button
          onClick={checkNow}
          disabled={checking}
          data-testid="pix-check-now-btn"
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#0B2B62] hover:bg-[#08204a] disabled:opacity-50 text-white text-xs font-bold rounded-md transition-colors"
        >
          {checking ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checando…</>
                    : <><RefreshCw className="w-3.5 h-3.5" /> Verificar agora</>}
        </button>
      </div>

      {!ready && (
        <div className="mt-3 pt-3 border-t border-amber-200 text-[11px] text-amber-900 flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          <div>
            Abra ticket em <a href="https://support.stripe.com/contact/email" target="_blank" rel="noreferrer"
              className="underline font-bold">support.stripe.com</a> pedindo ativação PIX.
            Enquanto isso, checkouts fazem <b>fallback automático para cartão</b> — pagamentos continuam funcionando.
          </div>
        </div>
      )}
    </div>
  );
};

export default PixStatusWidget;
export { PixStatusWidget };
